import { useLayoutEffect, useRef, type CSSProperties, type RefObject } from 'react';
import { SKELETON_DELAY_MS } from './useRevealGroup';

/**
 * A placeholder that replaces one the user could already see (an access gate's
 * skeleton handing over to the page's data skeleton, a spinner handing over to
 * a section's rows) appears at once, so the page does not blink blank between
 * the two. Only a replacement counts: a placeholder elsewhere on the page that
 * is merely still visible does not let a new one skip its delay.
 */
const HANDOVER_GRACE_MS = 100;

let lastVisibleUnmountAt = Number.NEGATIVE_INFINITY;

interface SkeletonAppearance<T extends HTMLElement> {
  ref: RefObject<T | null>;
  className: string;
  style: CSSProperties;
}

/**
 * The appearance of a loading placeholder (`SkeletonRegion`, `LoadingState`):
 * invisible for SKELETON_DELAY_MS so a fast read never flashes one, unless it
 * replaces a placeholder that was visible, when it continues that one. Attach
 * all three returned props to the placeholder's root element.
 */
export function useSkeletonAppearance<T extends HTMLElement>(): SkeletonAppearance<T> {
  const ref = useRef<T>(null);

  // A layout effect: React runs an outgoing placeholder's layout cleanup before
  // an incoming one's layout effect in the same commit, and this runs before
  // paint, so a handover never shows a blank frame. The delay is cancelled on
  // the element directly; React leaves an inline style it did not set alone.
  useLayoutEffect(() => {
    const handover = performance.now() - lastVisibleUnmountAt < HANDOVER_GRACE_MS;
    if (handover && ref.current) ref.current.style.animation = 'none';
    const visibleAt = performance.now() + (handover ? 0 : SKELETON_DELAY_MS);
    return () => {
      if (performance.now() >= visibleAt) lastVisibleUnmountAt = performance.now();
    };
  }, []);

  return {
    ref,
    className: 'animate-skeleton-in',
    style: { animationDelay: `${SKELETON_DELAY_MS}ms` },
  };
}
